/* =====================================================================
 * chatRenderPrefetch — one-shot prefetch of heavy chat render chunks.
 *
 * In P11-4f the helper accepted a host-provided list of loaders so the
 * shared package did not need to know the concrete file paths.  After
 * P11-4h-pass2 migrated `MessageBubble` and `MarkdownRenderer` into
 * this package, the loaders live right next to the helper and it can
 * self-serve the default list — hosts no longer have to mirror a
 * `chatRenderLoaders` constant.
 *
 * The signature remains compatible: callers may still pass an override
 * list for custom prefetch scenarios (tests, feature-flagged
 * experiments, tenant-specific extensions).
 * ===================================================================== */

export type ChatRenderPrefetchLoader = () => Promise<unknown>;

const DEFAULT_LOADERS: ReadonlyArray<ChatRenderPrefetchLoader> = [
  () => import('../components/chat/bubble/MessageBubble'),
  () => import('../components/chat/MarkdownRenderer'),
];

let prefetchPromise: Promise<void> | null = null;

export function prefetchChatRenderModules(
  loaders: ReadonlyArray<ChatRenderPrefetchLoader> = DEFAULT_LOADERS,
): void {
  if (prefetchPromise) return;
  if (loaders.length === 0) return;

  prefetchPromise = Promise.all(loaders.map((load) => load()))
    .then(() => undefined)
    .catch(() => {
      // 실패 시 다음 기회에 재시도할 수 있도록 잠금 해제
      prefetchPromise = null;
    });
}

/**
 * Reset the internal prefetch lock. Exposed for tests and auth-logout
 * flows that want the next mount to re-trigger prefetch from scratch.
 */
export function resetChatRenderPrefetch(): void {
  prefetchPromise = null;
}
